// The page's guest-session binding, and the channel that tells other tabs when it ends.
// A reset in one tab resets every other tab with reason "other-tab" (which is never re-announced).
const QUIET_REASONS = new Set(["other-tab", "binding-changed"]);

function openChannel(name, Impl) {
  if (typeof Impl !== "function") return null;
  try {
    return new Impl(name);
  } catch {
    return null;
  }
}

export function createGuestSession({ channelName = "kinlo-guest", BroadcastChannelImpl = globalThis.BroadcastChannel } = {}) {
  const listeners = new Set();
  let binding = null;

  function emit(reason, previousBinding) {
    for (const listener of [...listeners]) listener({ reason, previousBinding });
  }

  function reset(reason, { announce = true } = {}) {
    const previousBinding = binding;
    binding = null;
    emit(reason, previousBinding);
    if (announce && !QUIET_REASONS.has(reason)) {
      try {
        channel?.postMessage({ type: "session-changed" });
      } catch {
        // A closed channel only means no other tab hears about it.
      }
    }
  }

  const onMessage = (event) => {
    if (event?.data?.type === "session-changed") reset("other-tab", { announce: false });
  };
  const channel = openChannel(channelName, BroadcastChannelImpl);
  channel?.addEventListener("message", onMessage);

  return {
    get binding() {
      return binding;
    },
    observe(next) {
      if (binding === null) {
        binding = next;
        return "first";
      }
      if (binding === next) return "same";
      const previousBinding = binding;
      emit("binding-changed", previousBinding);
      binding = next;
      return "changed";
    },
    reset,
    onReset(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      channel?.removeEventListener("message", onMessage);
      channel?.close();
      listeners.clear();
    },
  };
}
